import { Component, h, escapeHtml, register } from './core.js';
import { renderIcon, iconHtml } from './icons.js';

/**
 * StageBar: a chevron strip showing where a record sits in a sequence of
 * stages (pipeline, workflow, kanban columns).
 *
 * new dfc.StageBar(container, {
 *   stages: [{ id, label, icon }], current: id,          // icon: any icon spec
 *   completed: 'before-current' | [ids], clickable: false, size: 'md' | 'sm', showIcons: true,
 *   icons: { complete: 'check', current: 'star', upcoming: 'minus' }
 * })
 * Events: select { id, stage } (user clicked; the host decides whether to set value), valueChanged { value, previous, source }
 * Static: dfc.StageBar.renderStatic(stages, current, opts) -> HTML string
 */
export class StageBar extends Component {
	static get componentName() { return 'stagebar'; }

	defaults() {
		return { stages: [], current: null, value: undefined, completed: 'before-current', clickable: false, size: 'md', showIcons: true, icons: { complete: 'check', current: 'star', upcoming: 'minus' } };
	}

	render() {
		const o = this.options;
		if (o.value !== undefined && !this._valueApplied) { o.current = o.value; this._valueApplied = true; }
		this.el.className = `dfc dfc-stagebar dfc-size-${o.size}` + (o.clickable ? ' is-clickable' : '') + (o.className ? ' ' + o.className : '');
		this.el.innerHTML = '';
		const list = h('ol', { className: 'dfc-stagebar-list', role: 'list' });
		const states = StageBar._states(o.stages, o.current, o.completed);
		o.stages.forEach((stage, i) => {
			const state = states[i];
			const icon = !o.showIcons ? null : renderIcon(StageBar._iconFor(stage, state, o.icons));
			const inner = h(o.clickable ? 'button' : 'span', {
				type: o.clickable ? 'button' : null, className: 'dfc-stagebar-stage', dataset: { id: String(stage.id) }, title: stage.label,
				'aria-current': state === 'current' ? 'step' : null,
			}, icon, h('span', { className: 'dfc-stagebar-label', text: stage.label }));
			list.appendChild(h('li', { className: `dfc-stagebar-item is-${state}` + (i === 0 ? ' is-first' : '') + (i === o.stages.length - 1 ? ' is-last' : ''), role: 'listitem' }, inner));
		});
		this.el.appendChild(list);
		const cur = list.querySelector('.is-current');
		if (cur && typeof cur.scrollIntoView === 'function') {
			try { cur.scrollIntoView({ block: 'nearest', inline: 'center' }); } catch (e) { /* older browsers */ }
		}
	}

	bind() {
		this._onClick = (e) => {
			if (!this.options.clickable) return;
			const btn = e.target.closest && e.target.closest('.dfc-stagebar-stage');
			if (!btn || !this.el.contains(btn)) return;
			const stage = this.options.stages.find((s) => String(s.id) === btn.dataset.id);
			this.emit('select', { id: stage ? stage.id : btn.dataset.id, stage });
		};
		this.el.addEventListener('click', this._onClick);
	}

	unbind() { if (this.el) this.el.removeEventListener('click', this._onClick); }

	get current() { return this.options.current; }
	set current(id) { this.value = id; }
	/** value = the current stage id. */
	get value() { return this.options.current; }
	set value(id) { const previous = this.options.current; this.update({ current: id }); this._valueChanged(id, previous, 'api'); }

	static _iconFor(stage, state, icons) {
		const set = Object.assign({ complete: 'check', current: 'star', upcoming: 'minus' }, icons || {});
		const own = stage.icon || (stage.iconCss ? { className: stage.iconCss } : null);
		if (state === 'complete') return set.complete;
		return own || set[state];
	}

	static _states(stages, current, completed) {
		const curIdx = stages.findIndex((s) => String(s.id) === String(current));
		return stages.map((s, i) => {
			if (i === curIdx) return 'current';
			if (Array.isArray(completed)) return completed.map(String).includes(String(s.id)) ? 'complete' : 'upcoming';
			return curIdx >= 0 && i < curIdx ? 'complete' : 'upcoming';
		});
	}

	/** Read-only HTML for grid cells and summaries. */
	static renderStatic(stages, current, opts = {}) {
		const states = StageBar._states(stages, current, opts.completed || 'before-current');
		let html = `<span class="dfc dfc-stagebar dfc-size-${opts.size || 'sm'} is-static"><ol class="dfc-stagebar-list" role="list">`;
		stages.forEach((s, i) => {
			const st = states[i];
			const icon = opts.showIcons === false ? '' : iconHtml(StageBar._iconFor(s, st, opts.icons));
			html += `<li class="dfc-stagebar-item is-${st}${i === 0 ? ' is-first' : ''}${i === stages.length - 1 ? ' is-last' : ''}" role="listitem"><span class="dfc-stagebar-stage" title="${escapeHtml(s.label)}"${st === 'current' ? ' aria-current="step"' : ''}>${icon}<span class="dfc-stagebar-label">${escapeHtml(s.label)}</span></span></li>`;
		});
		return html + '</ol></span>';
	}
}

register('stagebar', StageBar);
